import { TextField } from 'braid-design-system';
import {
  forwardRef,
  type ComponentProps,
  type ForwardedRef,
  type ReactEventHandler,
} from 'react';
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';

type TextFieldProps = Omit<
  ComponentProps<typeof TextField>,
  'id' | 'value' | 'onChange'
>;

interface InputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  onChange?: ReactEventHandler<HTMLInputElement>;
}

const Input = <T extends FieldValues>(
  props: InputProps<T> & TextFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const { control, name, label, onChange, ...restProps } = props;
  const { field, fieldState } = useController({
    name,
    control,
  });

  const { ref: fieldRef, value, onChange: fieldOnChange, ...restField } = field;

  const handleChange: ReactEventHandler<HTMLInputElement> = (e) => {
    fieldOnChange(e);
    onChange?.(e);
  };

  return (
    <TextField
      ref={(input) => {
        fieldRef(input);
        if (typeof ref === 'function') {
          ref(input);
        } else if (ref) {
          ref.current = input;
        }
      }}
      id={field.name}
      label={label}
      tone={fieldState.invalid ? 'critical' : 'neutral'}
      message={fieldState.error?.message}
      value={value as string}
      onChange={handleChange}
      {...restField}
      {...restProps}
    />
  );
};

const FormTextField = forwardRef(Input) as <T extends FieldValues>(
  props: InputProps<T> &
    TextFieldProps & { ref?: ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof Input>;

export default FormTextField;
